<script setup lang="ts">
import { useAlmost } from '@/hooks/useAlmost';
import { useScreenSize } from '@/hooks/useScreen';
import { useAuthStore } from '@/stores/auth/auth.slice';
import { SignupStaffPayloadDto } from '@/types/request.dto';
import { useToast } from 'primevue';
import { ref, useTemplateRef } from 'vue';

// props
// emits
// store
const authStore = useAuthStore();
const { isMobile } = useScreenSize();
const toast = useToast();
const { showAlmost } = useAlmost();
// state & computed
const emailRef = useTemplateRef('signup-email'),
  pswRef = useTemplateRef('signup-psw'),
  cpswRef = useTemplateRef('signup-cpsw'),
  nameRef = useTemplateRef('signup-name');
const showPsw = ref(false),
  showCpsw = ref(false),
  isLoading = ref(false);

// functions
const handleSignup = async (e: Event) => {
  e.preventDefault();
  if (showAlmost)
    return showAlmost(
      'Veuillez contact le service client pour créer un compte',
    );

  const secret = pswRef.value.value;
  if (secret != cpswRef.value.value)
    return toast.add({ life: 3_000, summary: 'Mots de passe différents' });

  const payload: SignupStaffPayloadDto = {
    secret: pswRef.value.value,
    email: emailRef.value.value,
    fullname: nameRef.value.value,
  };
  isLoading.value = true;
  await authStore
    .signup(payload)
    .then(({ data }) => {
      if ('error' in data) throw data;
    })
    .catch((error) => {
      if ('message' in error && error.message instanceof Array) {
        const message = (error?.message as string[]).map((str) =>
          str == 'EMAIL_ALLREADY_USED' ? 'Cet email est déjà utilisé' : str,
        );
        toast.add({ life: 3_000, summary: message?.join(',') });
      }
    })
    .finally(() => (isLoading.value = false));
};

// watch
// mount
</script>

<template>
  <div
    class="text-grand-text flex h-[100dvh] w-full flex-col justify-start overflow-y-auto pt-[2rem] md:flex-row md:justify-center md:pt-[1rem] md:pb-[1rem] 2xl:px-[2rem]"
  >
    <div v-if="isMobile" class="grow"></div>

    <div v-show="isMobile" class="mb-[.5rem] self-center">
      <img :src="logo.logo_small" alt="logo" srcset="" class="logo-bg-radial" />
    </div>

    <div v-if="isMobile" class="grow"></div>

    <div
      class="border-beige-clair/50 flex max-w-[56rem] gap-[2rem] rounded-t-[30px] bg-white p-[1rem] md:m-auto md:w-[60vw] md:rounded-b-[30px] md:border md:shadow-[0_2px_4px_0_#FDE8AF]"
    >
      <div v-show="!isMobile" class="basis-1/2">
        <img
          :src="retail_checkout_img"
          alt="retail_checkout_img"
          srcset=""
          class="aspect-[429/722] max-h-full max-w-full rotate-y-180 rounded-[30px] object-cover object-center"
        />
      </div>
      <div :class="!isMobile ? 'basis-1/2' : ''" class="grow">
        <form
          @submit.prevent="handleSignup"
          class="mx-auto flex h-full max-w-[22rem] flex-col items-stretch justify-center"
        >
          <img
            v-show="!isMobile"
            :src="logo.logo_small"
            alt=""
            class="self-center"
          />

          <div class="my-[.5rem]"></div>

          <h3 class="self-center text-[20px] font-[500]">Créer un compte</h3>

          <div class="my-[.5rem]"></div>

          <h3 class="text-secondary-text self-center">
            Gérez plus rapidement votre restaurant
          </h3>

          <div class="my-[.5rem]"></div>

          <div class="flex flex-col gap-[.3rem]">
            <label
              for="signup-name"
              class="text-grand-text text-[14px] font-[500]"
            >
              Nom complet
            </label>
            <input
              type="text"
              required
              autocomplete="name"
              id="signup-name"
              ref="signup-name"
              placeholder="Agano Moise"
              class="border-secondary-text/20 rounded-[10px] border-[1.5px] p-[.8rem]"
            />
          </div>

          <div class="my-[.5rem]"></div>

          <div class="flex flex-col gap-[.3rem]">
            <label
              for="signup-email"
              class="text-grand-text text-[14px] font-[500]"
            >
              Email
            </label>
            <input
              type="email"
              required
              id="signup-email"
              autocomplete="email"
              ref="signup-email"
              placeholder="Ex: aganomoise47@gmail.com"
              class="border-secondary-text/20 rounded-[10px] border-[1.5px] p-[.8rem]"
            />
          </div>

          <div class="my-[.5rem]"></div>

          <div class="relative flex flex-col gap-[.3rem]">
            <button
              type="button"
              class="absolute right-[.5rem] bottom-[.4rem] p-[.4rem]"
              @click="
                () => {
                  showPsw = !showPsw;
                  pswRef?.focus?.();
                }
              "
            >
              <v-icon :name="showPsw ? 'custom-eye-2' : 'custom-eye-slash-2'" />
            </button>
            <label
              for="signup-psw"
              class="text-grand-text text-[14px] font-[500]"
            >
              Mot de passe
            </label>
            <input
              :type="showPsw ? 'text' : 'password'"
              required
              autocomplete="new-password"
              id="signup-psw"
              ref="signup-psw"
              placeholder="Tapez votre mot de passe"
              class="border-secondary-text/20 rounded-[10px] border-[1.5px] p-[.8rem]"
            />
          </div>
          <div class="my-[.5rem]"></div>

          <div class="relative flex flex-col gap-[.3rem]">
            <label
              for="signup-cpsw"
              class="text-grand-text text-[14px] font-[500]"
            >
              Confirmer mot de passe
            </label>
            <input
              :type="showCpsw ? 'text' : 'password'"
              required
              autocomplete="new-password"
              id="signup-cpsw"
              ref="signup-cpsw"
              placeholder="Retapez votre mot de passe"
              class="border-secondary-text/20 rounded-[10px] border-[1.5px] p-[.8rem]"
            />
            <button
              type="button"
              class="absolute right-[.5rem] bottom-[.4rem] p-[.4rem]"
              @click="
                () => {
                  showCpsw = !showCpsw;
                  cpswRef?.focus?.();
                }
              "
            >
              <v-icon
                :name="showCpsw ? 'custom-eye-2' : 'custom-eye-slash-2'"
              />
            </button>
          </div>
          <div class="my-[.5rem]"></div>

          <div class="flex items-center gap-[.3rem]">
            <input
              type="checkbox"
              required
              id="terms-accept"
              ref="terms-accept"
            />
            <label for="terms-accept" class="text-grand-text text-[14px]">
              En vous inscrivant, vous acceptez
              <RouterLink
                to="#"
                class="text-primary-700 cursor-pointer underline"
                >nos conditions d'utilisation</RouterLink
              >
              et notre
              <RouterLink
                to="#"
                class="text-primary-700 cursor-pointer underline"
                >politique de confidentialité.</RouterLink
              >
            </label>
          </div>

          <div class="my-[.5rem]"></div>

          <button
            type="submit"
            :disabled="isLoading"
            :class="{
              'cursor-progress': isLoading,
              'cursor-pointer': !isLoading,
            }"
            class="linear-gr-rect cursor-pointer rounded-[10px] p-[1rem] text-center font-[500] text-white"
          >
            <span
              v-if="isLoading"
              class="inline-block h-[1rem] w-[1rem] animate-spin rounded-full border border-transparent border-t-white"
            ></span
            ><span v-else>S'inscrire</span>
          </button>

          <div class="my-[.5rem]"></div>

          <div class="flex items-center justify-between gap-1">
            <hr class="border-secondary-text/20 grow border-t" />
            <span class="text-secondary-text">Ou vous continuez avec</span>
            <hr class="border-secondary-text/20 grow border-t" />
          </div>

          <div class="my-[.5rem]"></div>

          <div class="flex justify-between gap-[1rem]">
            <RouterLink
              class="border-secondary-text/20 flex basis-1/2 items-center justify-center gap-[1rem] rounded-[10px] border p-[1rem]"
              to="#"
            >
              <v-icon name="custom-google-2" fill="none" class="" scale="1.2" />
              <span class="font-[500]">Google</span>
            </RouterLink>
            <RouterLink
              class="border-secondary-text/20 flex basis-1/2 items-center justify-center gap-[1rem] rounded-[10px] border p-[1rem]"
              to="#"
            >
              <v-icon name="custom-apple-2" fill="none" class="" scale="1.2" />
              <span class="font-[500]">Apple</span>
            </RouterLink>
          </div>

          <div class="my-[.5rem]"></div>

          <div class="self-center">
            <span>Vous avez déjà un compte? </span>
            <RouterLink
              to="/login"
              class="text-primary-700 cursor-pointer underline"
            >
              Connectez-vous.
            </RouterLink>
          </div>

          <div class="my-[2rem] md:my-0"></div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
const retail_checkout_img =
  'https://res.cloudinary.com/nextepsol/image/upload/v1750436753/retail_l2ninl.jpg';
const logo = {
  logo_img:
    'https://res.cloudinary.com/nextepsol/image/upload/v1750433813/logo_jjuwqh.svg',
  logo_small_shadow:
    'https://res.cloudinary.com/nextepsol/image/upload/v1750758890/logo_small_shadow_wvq386.svg',
  logo_small:
    'https://res.cloudinary.com/nextepsol/image/upload/v1750758888/logo_small_v86ay2.svg',
};
</script>

<style scoped>
input:focus {
  outline-color: var(--color-primary-700);
  outline-width: 2px;
  border-color: transparent;
  outline-style: solid;
  caret-color: var(--color-primary-700);
  transition: outline 200ms ease-in;
}
input {
  outline-color: var(--color-primary-100);
}

.logo-bg-radial {
  background-image: radial-gradient(
    circle at 50% 55%,
    var(--color-primary-500) 0%,
    #0000 65%
  );
  padding-bottom: 20px;
}
</style>
